import React, { useContext } from 'react';
import Work from '@mui/icons-material/Work';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import CancelIcon from '@mui/icons-material/Cancel';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import debounce from 'lodash.debounce';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import ActivityFieldsSummary from './ActivityFieldsSummary';
import { RightsContext } from 'providers/RightsContext';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Checkbox, FormHelperText } from '@mui/material';
import { Button, IconButton, Autocomplete } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { GridActionsWrapper } from 'components/common/CommonStyles';

const ActivityFieldsComponentAlpha = ({
  onActivityDomainsCardExpanded,
  activityDomainsCardExpanded,
  documentId,
  siteId,
  partnerId,
  saveDisabled,
  t,
  addActivityField,
  activityFields,
  onPropertyChange,
  loadActivityFieldsTypes,
  deleteActivityField,
  saveActivityFields,
  validDefaultActivitySelection,
  validActivityFieldMultipleSelection,
  validationActivityField,
  saving
}) => {
  const { readOnlyFull, clientActivityfieldsWrite } = useContext(RightsContext);

  return (
    <CollapseOrBlankCard
      id={'offerPartnerActivityFieldsCard'}
      title={t('OfferEdit.Client.ActivityFields.Title')}
      subheader={<ActivityFieldsSummary activityFields={activityFields} />}
      hideSubheaderOnExpand
      icon={Work}
      expanded={activityDomainsCardExpanded}
      onToggle={onActivityDomainsCardExpanded}
      canExpand={partnerId != null}
      content={
        <Grid container>
          <Grid item xs={12} sm={12} md={12}>
            <CardNoResultsWrapper content={activityFields}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={6}>
                      <strong>{t('OfferEdit.Client.ActivityFields.CAENCode')}</strong>
                    </TableCell>
                    <TableCell colSpan={2} align='center'>
                      <strong>{t('OfferEdit.Client.ActivityFields.MainActivity')}</strong>
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityFields.map((item, idx) => {
                    const activityFieldValue = !item.activityFieldId
                      ? null
                      : {
                          value: item.activityFieldId,
                          label:
                            item.activityFieldCode && item.activityFieldName ? `${item.activityFieldCode} ${item.activityFieldName}` : null
                        };

                    return (
                      <TableRow key={idx}>
                        <TableCell colSpan={6}>
                          <div id={'offerPartnerActivityFieldsCard_activityField_' + idx}>
                            <Autocomplete
                              fullWidth
                              isSearchable
                              isClearable
                              value={activityFieldValue}
                              valueKey={'value'}
                              labelKey={'label'}
                              onChange={onPropertyChange(idx)('activityFieldId', 'activityFieldName')}
                              loadOptions={value => {
                                const func = debounce(loadActivityFieldsTypes, 500, { leading: true });
                                return func(value || activityFieldValue?.label);
                              }}
                              error={!isValid(validationActivityField[idx].activityFieldId)}
                              helperText={getErrors(validationActivityField[idx].activityFieldId)}
                              disabled={readOnlyFull || !clientActivityfieldsWrite}
                            />
                          </div>
                        </TableCell>
                        <TableCell colSpan={2} align='center'>
                          <Checkbox
                            id={'offerPartnerActivityFieldsCard_defaultActivity_' + idx}
                            checked={item.defaultActivity || false}
                            name='defaultActivity'
                            onChange={onCheckBoxChange(onPropertyChange(idx)('defaultActivity'))}
                            disabled={readOnlyFull || !clientActivityfieldsWrite}
                          />
                        </TableCell>
                        <TableCell colSpan={2} align='right'>
                          <IconButton
                            id={'offerPartnerActivityFieldsCard_deleteBtn_' + idx}
                            color='primary'
                            variant='text'
                            size='small'
                            onClick={() => deleteActivityField(idx)}
                            disabled={readOnlyFull || !clientActivityfieldsWrite}
                          >
                            <CancelIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardNoResultsWrapper>
            {!validDefaultActivitySelection && (
              <FormHelperText error={true} id='name-error-text'>
                {t('OfferEdit.Client.ActivityFields.ValidateDefaultActivity')}
              </FormHelperText>
            )}
            {!validActivityFieldMultipleSelection && (
              <FormHelperText error={true} id='name-error-text'>
                {t('OfferEdit.Client.ActivityFields.ValidateActivityFieldId')}
              </FormHelperText>
            )}
            <GridActionsWrapper mt={2}>
              <Button
                id={'offerPartnerActivityFieldsCard_addBtn'}
                disabled={readOnlyFull || !clientActivityfieldsWrite}
                onClick={addActivityField}
              >
                {t('Add')}
              </Button>
              <Button
                id={'offerPartnerActivityFieldsCard_saveBtn'}
                onClick={() => saveActivityFields(documentId, siteId, partnerId, activityFields)}
                disabled={readOnlyFull || saveDisabled || !isValid(validationActivityField) || !clientActivityfieldsWrite || saving}
              >
                {saving ? t('Saving') : t('Save')}
              </Button>
            </GridActionsWrapper>
          </Grid>
        </Grid>
      }
    />
  );
};

ActivityFieldsComponentAlpha.propTypes = {
  partnerId: PropTypes.number,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  siteid: PropTypes.number,
  onActivityDomainsCardExpanded: PropTypes.func,
  activityDomainsCardExpanded: PropTypes.bool,
  saveDisabled: PropTypes.bool,
  t: PropTypes.func.isRequired,
  addActivityField: PropTypes.func.isRequired,
  loadActivityFieldsTypes: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  activityFields: PropTypes.array.isRequired,
  deleteActivityField: PropTypes.func.isRequired,
  validActivityFieldMultipleSelection: PropTypes.bool,
  saveActivityFields: PropTypes.func.isRequired,
  validDefaultActivitySelection: PropTypes.bool,
  validationActivityField: PropTypes.object,
  saving: PropTypes.bool.isRequired
};
export default compose(withTranslation())(ActivityFieldsComponentAlpha);
